import { type ApolloClient } from '@apollo/client';
import { type INotificationCallback } from '@dashboard/components/notifications';
import { type UserFragment } from '@dashboard/graphql';
import useLocalStorage from '@dashboard/hooks/useLocalStorage';
import useNavigator from '@dashboard/hooks/useNavigator';
import { commonMessages } from '@dashboard/intl';
import {
  checkIfCredentialsExist,
  isSupported as isCredentialsManagementAPISupported,
  login as loginWithCredentialsManagementAPI,
  saveCredentials,
} from '@dashboard/utils/credentialsManagement';
import { useEffect, useReducer, useRef, useState } from 'react';
import { type IntlShape } from 'react-intl';

import { authApi, type AuthUser } from '../authApi';
import { NUXNODE_AUTH_LOGOUT_EVENT } from '../restClient';
import { getRefreshToken } from '../storage';
import {
  type AccountErrorEntry,
  type ExternalLoginInput,
  type ExternalLoginResult,
  type LoginResult,
  type RequestExternalLoginInput,
  type RequestExternalLoginResult,
  type UserContext,
  type UserContextError,
} from '../types';
import { useLastLoginMethod } from './useLastLoginMethod';

// ---------------------------------------------------------------------------
// Error-code mappings (preserved from the GraphQL implementation so existing
// translation keys keep working).
// ---------------------------------------------------------------------------

const accountErrorCode = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  LOGIN_ATTEMPT_DELAYED: 'LOGIN_ATTEMPT_DELAYED',
} as const;

type AuthErrorCode = (typeof accountErrorCode)[keyof typeof accountErrorCode] | string;

function mapServerErrors(errors: AccountErrorEntry[]): UserContextError[] {
  return errors.map(({ code }) => {
    switch (code as AuthErrorCode) {
      case accountErrorCode.INVALID_CREDENTIALS:
        return 'invalidCredentials';
      case accountErrorCode.LOGIN_ATTEMPT_DELAYED:
        return 'loginAttemptDelay';
      default:
        return 'loginError';
    }
  });
}

// ---------------------------------------------------------------------------
// Adapter — REST ``AuthUser`` payloads need to fit the legacy
// ``UserFragment`` shape so all downstream consumers (sidebars, layout,
// permissions checks) work unchanged.  We add ``__typename`` for Apollo
// cache compatibility.  ``avatar.url`` is the generated gradient SVG.
// ---------------------------------------------------------------------------

function authUserToFragment(user: AuthUser | null): UserFragment | null {
  if (!user) return null;

  return {
    __typename: 'User',
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    isStaff: user.isStaff,
    dateJoined: user.dateJoined,
    restrictedAccessToChannels: user.restrictedAccessToChannels,
    metadata: (user.metadata ?? []).map((item) => ({
      __typename: 'MetadataItem' as const,
      key: item.key,
      value: item.value,
    })),
    userPermissions: (user.userPermissions ?? []).map((perm) => ({
      __typename: 'UserPermission' as const,
      code: perm.code as UserFragment['userPermissions'][number]['code'],
      name: perm.name,
    })),
    avatar: user.avatar?.url
      ? { __typename: 'Image' as const, url: user.avatar.url }
      : null,
    accessibleChannels: (user.accessibleChannels ?? []).map((channel) => ({
      __typename: 'Channel' as const,
      id: channel.id,
      isActive: channel.isActive,
      name: channel.name,
      slug: channel.slug,
      currencyCode: channel.currencyCode,
      defaultCountry: {
        __typename: 'CountryDisplay' as const,
        code: channel.defaultCountry.code,
        country: channel.defaultCountry.country,
      },
      stockSettings: {
        __typename: 'StockSettings' as const,
        allocationStrategy: channel.stockSettings
          .allocationStrategy as UserFragment['accessibleChannels'][number]['stockSettings']['allocationStrategy'],
      },
    })),
  } as UserFragment;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: UserFragment | null;
  errors: UserContextError[];
}

type AuthAction =
  | { type: 'BOOTSTRAP_START' }
  | { type: 'BOOTSTRAP_RESULT'; user: UserFragment | null }
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; user: UserFragment | null }
  | { type: 'LOGIN_FAILURE'; errors: UserContextError[] }
  | { type: 'LOGOUT' }
  | { type: 'REFRESH_USER'; user: UserFragment | null }
  | { type: 'SET_ERRORS'; errors: UserContextError[] };

const initialState: AuthState = {
  status: 'idle',
  user: null,
  errors: [],
};

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'BOOTSTRAP_START':
      return { ...state, status: 'authenticating', errors: [] };
    case 'BOOTSTRAP_RESULT':
      return {
        ...state,
        status: action.user ? 'authenticated' : 'unauthenticated',
        user: action.user,
        errors: [],
      };
    case 'LOGIN_START':
      return { ...state, status: 'authenticating', errors: [] };
    case 'LOGIN_SUCCESS':
      return { status: 'authenticated', user: action.user, errors: [] };
    case 'LOGIN_FAILURE':
      return { ...state, status: 'unauthenticated', errors: action.errors };
    case 'LOGOUT':
      return { status: 'unauthenticated', user: null, errors: [] };
    case 'REFRESH_USER':
      return { ...state, user: action.user };
    case 'SET_ERRORS':
      return { ...state, errors: action.errors };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseAuthProviderOpts {
  intl: IntlShape;
  notify: INotificationCallback;
  apolloClient: ApolloClient<unknown>;
}

export function useAuthProvider({ intl, notify, apolloClient }: UseAuthProviderOpts): UserContext {
  const navigate = useNavigator();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [requestedExternalPluginId] = useLocalStorage('requestedExternalPluginId', null);
  const [isCredentialsLogin, setIsCredentialsLogin] = useState(false);
  const permitCredentialsAPI = useRef(true);
  const { setLastLoginMethod } = useLastLoginMethod();
  const apolloRef = useRef(apolloClient);

  apolloRef.current = apolloClient;

  // Bootstrap — rehydrate the session from the stored refresh token, if any.
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!getRefreshToken()) {
        dispatch({ type: 'BOOTSTRAP_RESULT', user: null });

        return;
      }

      dispatch({ type: 'BOOTSTRAP_START' });

      const refresh = await authApi.refreshToken();

      if (cancelled) return;

      if (refresh.errors.length > 0) {
        dispatch({ type: 'BOOTSTRAP_RESULT', user: null });

        return;
      }

      const meResult = await authApi.me();

      if (cancelled) return;

      dispatch({
        type: 'BOOTSTRAP_RESULT',
        user: authUserToFragment(meResult.data ?? null),
      });
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for forced logouts coming from the REST interceptor (refresh
  // failure dispatches ``nuxnodeAuth:logout`` so unrelated business
  // queries don't have to know about token state).
  useEffect(() => {
    const handler = () => {
      apolloRef.current?.clearStore?.();
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener(NUXNODE_AUTH_LOGOUT_EVENT, handler);

    return () => {
      window.removeEventListener(NUXNODE_AUTH_LOGOUT_EVENT, handler);
    };
  }, []);

  useEffect(() => {
    if (state.status === 'authenticated') {
      permitCredentialsAPI.current = true;
    }
  }, [state.status]);

  // Trigger native credentials-manager login on cold-load when the
  // browser supports it and we don't already have a session.
  useEffect(() => {
    if (
      state.status === 'unauthenticated' &&
      !requestedExternalPluginId &&
      permitCredentialsAPI.current
    ) {
      permitCredentialsAPI.current = false;
      loginWithCredentialsManagementAPI(handleLogin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  // -------------------------------------------------------------------------
  // Operations
  // -------------------------------------------------------------------------

  async function handleLogout() {
    try {
      await authApi.logout();
    } catch {
      // The server-side blacklist is best-effort; even if the call
      // fails we still want to clear local state below.
    }

    const hasCredentials = await checkIfCredentialsExist();

    if (isCredentialsManagementAPISupported && hasCredentials) {
      navigator.credentials.preventSilentAccess();
    }

    apolloRef.current?.clearStore?.();
    dispatch({ type: 'LOGOUT' });
    navigate('/');
  }

  async function logoutNonStaffUser(user: UserFragment | null) {
    if (user && !user.isStaff) {
      notify({
        status: 'error',
        text: intl.formatMessage(commonMessages.unauthorizedDashboardAccess),
        title: intl.formatMessage(commonMessages.insufficientPermissions),
      });
      await handleLogout();
    }
  }

  async function handleLogin(email: string, password: string): Promise<LoginResult | undefined> {
    if (isCredentialsLogin) {
      return undefined;
    }

    setIsCredentialsLogin(true);
    dispatch({ type: 'LOGIN_START' });

    try {
      const tokenResult = await authApi.login({ email, password });

      if (tokenResult.errors.length > 0) {
        const userContextErrors = mapServerErrors(tokenResult.errors);

        dispatch({
          type: 'LOGIN_FAILURE',
          errors: userContextErrors.length > 0 ? userContextErrors : ['loginError'],
        });

        return { errors: tokenResult.errors, user: null };
      }

      const user = authUserToFragment(tokenResult.data?.user ?? null);
      const hasNoPermissions = !user?.userPermissions?.length;

      if (hasNoPermissions) {
        dispatch({ type: 'SET_ERRORS', errors: ['noPermissionsError'] });
        await handleLogout();

        return { errors: tokenResult.errors, user };
      }

      saveCredentials(user!, password);
      dispatch({ type: 'LOGIN_SUCCESS', user });

      await logoutNonStaffUser(user);

      return { errors: tokenResult.errors, user };
    } catch {
      dispatch({ type: 'LOGIN_FAILURE', errors: ['unknownLoginError'] });

      return undefined;
    } finally {
      setIsCredentialsLogin(false);
    }
  }

  async function handleRequestExternalLogin(
    pluginId: string,
    input: RequestExternalLoginInput,
  ): Promise<RequestExternalLoginResult | undefined> {
    let stringifyInput: string;

    try {
      stringifyInput = JSON.stringify(input);
    } catch {
      dispatch({ type: 'SET_ERRORS', errors: ['externalLoginError'] });

      return undefined;
    }

    const result = await authApi.externalAuthUrl({ pluginId, input: stringifyInput });

    return {
      authenticationData: result.data?.authenticationData ?? '',
      errors: result.errors,
    };
  }

  async function handleExternalLogin(
    pluginId: string | null,
    input: ExternalLoginInput,
  ): Promise<ExternalLoginResult | undefined> {
    if (!pluginId) {
      return undefined;
    }

    try {
      const result = await authApi.externalLogin({
        pluginId,
        input: JSON.stringify(input),
      });

      if (result.errors.length > 0) {
        dispatch({ type: 'SET_ERRORS', errors: ['externalLoginError'] });
        await handleLogout();

        return { errors: result.errors, user: null };
      }

      const user = authUserToFragment(result.data?.user ?? null);
      const hasNoPermissions = !user?.userPermissions?.length;

      if (hasNoPermissions) {
        dispatch({ type: 'SET_ERRORS', errors: ['noPermissionsError'] });
        await handleLogout();
      } else {
        dispatch({ type: 'LOGIN_SUCCESS', user });
        setLastLoginMethod(pluginId);
        await logoutNonStaffUser(user);
      }

      return {
        errors: result.errors,
        user,
        token: result.data?.token ?? null,
        refreshToken: result.data?.refreshToken ?? null,
        csrfToken: result.data?.csrfToken ?? null,
      };
    } catch {
      dispatch({ type: 'SET_ERRORS', errors: ['unknownLoginError'] });

      return undefined;
    }
  }

  async function refetchUser(): Promise<UserFragment | null> {
    const result = await authApi.me();
    const user = authUserToFragment(result.data ?? null);

    dispatch({ type: 'REFRESH_USER', user });

    return user;
  }

  return {
    login: handleLogin,
    requestLoginByExternalPlugin: handleRequestExternalLogin,
    loginByExternalPlugin: handleExternalLogin,
    logout: handleLogout,
    user: state.user,
    authenticating: state.status === 'authenticating' && state.errors.length === 0,
    isCredentialsLogin,
    authenticated:
      state.status === 'authenticated' && !!state.user?.isStaff && state.errors.length === 0,
    errors: state.errors,
    refetchUser,
  };
}
